import { useParams } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import {
  MapPinIcon,
  TruckIcon,
  PackageIcon,
  NavigationIcon,
  MoonIcon,
  ClockIcon,
  CheckCircle2Icon,
  AlertTriangleIcon,
  UserIcon,
} from "lucide-react"
import { cn } from "@/lib/utils"

// ─── Types ────────────────────────────────────────────────────────────────────

interface PublicShipment {
  id:             string
  internalId:     number
  status:         string
  workLine:       string | null
  clientName:     string | null
  cargoTypeName:  string | null
  senderName:     string | null
  senderCity:     string | null
  recipientName:  string | null
  recipientCity:  string | null
  driverName:     string | null
  truckLicensePlate: string | null
  truckUnitNumber:   number | null
  loadingWeightKg:    string | null
  unloadingWeightKg:  string | null
  deliveryNoteNumber: string | null
  originArrivalAt:        string | null
  originLoadingAt:        string | null
  originDepartureAt:      string | null
  destinationArrivalAt:   string | null
  destinationUnloadingAt: string | null
  tripPlan: Record<string, unknown> | null
}

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:3001"
const API_KEY = import.meta.env.VITE_API_KEY ?? ""

// ─── Helpers ──────────────────────────────────────────────────────────────────

const STATUS_LABELS: Record<string, string> = {
  en_planificacion: "En planificación",
  planificado:      "Planificado",
  activo:           "Activo",
  en_curso:         "En curso",
  finalizado:       "Finalizado",
}

const STATUS_COLORS: Record<string, string> = {
  en_planificacion: "bg-gray-100 text-gray-600",
  planificado:      "bg-blue-100 text-blue-700",
  activo:           "bg-emerald-100 text-emerald-700",
  en_curso:         "bg-orange-100 text-orange-700",
  finalizado:       "bg-slate-100 text-slate-600",
}

function fmtShort(iso: string | null | undefined) {
  if (!iso) return "—"
  return new Date(iso).toLocaleDateString("es-AR", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  })
}

// ─── Plan display sub-components ─────────────────────────────────────────────

function InfoRow({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null
  return (
    <div className="flex justify-between items-baseline gap-4 py-1.5 border-b border-gray-100 last:border-0">
      <span className="text-sm text-muted-foreground shrink-0">{label}</span>
      <span className="text-sm font-medium text-right">{value}</span>
    </div>
  )
}

function MilestoneRow({ label, value }: { label: string; value: string | null | undefined }) {
  const done = !!value
  return (
    <div className="flex items-center gap-3">
      <div className={cn(
        "size-5 rounded-full flex items-center justify-center shrink-0",
        done ? "bg-emerald-500" : "bg-gray-200",
      )}>
        {done && <CheckCircle2Icon className="size-3 text-white" />}
      </div>
      <div className="flex-1">
        <p className="text-xs font-medium text-muted-foreground">{label}</p>
        {done && <p className="text-xs text-emerald-700 font-medium">{fmtShort(value)}</p>}
      </div>
    </div>
  )
}

// ─── Weather helpers ──────────────────────────────────────────────────────────

const WEATHER_CONDITION_META: Record<string, { label: string; emoji: string }> = {
  soleado: { label: "Soleado",      emoji: "☀️"  },
  viento:  { label: "Viento +40km", emoji: "💨"  },
  lluvia:  { label: "Lluvia",       emoji: "🌧️"  },
  granizo: { label: "Granizo",      emoji: "🌨️"  },
  nieve:   { label: "Nieve",        emoji: "❄️"  },
  helada:  { label: "Helada",       emoji: "🥶"  },
}

const WEATHER_RATING_META: Record<string, { label: string; className: string; badgeClassName: string }> = {
  good:    { label: "Bueno",   className: "bg-green-50 border-green-200",  badgeClassName: "bg-green-100 text-green-800" },
  regular: { label: "Regular", className: "bg-gray-50 border-gray-200",   badgeClassName: "bg-gray-100 text-gray-600"  },
  bad:     { label: "Malo",    className: "bg-red-50 border-red-200",     badgeClassName: "bg-red-100 text-red-700"    },
}

const WEATHER_CONDITION_ORDER = ["soleado", "viento", "lluvia", "granizo", "nieve", "helada"]

function WeatherDayCard({ wday }: {
  wday: { day: number; date: string; ratings: Record<string, string> }
}) {
  const hasBad = Object.values(wday.ratings).some(r => r === "bad")
  const dateLabel = new Date(wday.date).toLocaleDateString("es-AR", {
    weekday: "short", day: "numeric", month: "short",
  })
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <div className="size-6 rounded-full bg-gray-900 flex items-center justify-center shrink-0">
          <span className="text-[11px] font-bold text-white">{wday.day}</span>
        </div>
        <span className="text-sm font-semibold">Día {wday.day}</span>
        <span className="text-xs text-muted-foreground">{dateLabel}</span>
        {hasBad && (
          <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-red-100 text-red-700 px-2 py-0.5 text-[10px] font-bold">
            ⚠️ Adversas
          </span>
        )}
      </div>
      <div className="grid grid-cols-3 gap-1.5">
        {WEATHER_CONDITION_ORDER.map(condId => {
          const meta   = WEATHER_CONDITION_META[condId]
          const rating = wday.ratings[condId] ?? "regular"
          const style  = WEATHER_RATING_META[rating] ?? WEATHER_RATING_META["regular"]!
          return (
            <div key={condId} className={cn("flex flex-col items-center gap-1 rounded-lg border px-2 py-2", style.className)}>
              <span className="text-lg leading-none">{meta?.emoji}</span>
              <span className="text-[9px] font-semibold text-center leading-tight uppercase text-muted-foreground">
                {meta?.label}
              </span>
              <span className={cn("text-[9px] font-bold rounded-full px-1.5 py-0.5", style.badgeClassName)}>
                {style.label}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function PublicTripPlanPage() {
  const { clientId, shipmentId } = useParams<{ clientId: string; shipmentId: string }>()

  const { data: shipment, isLoading, error } = useQuery<PublicShipment>({
    queryKey: ["public-trip-plan", clientId, shipmentId],
    queryFn:  async () => {
      const res = await fetch(
        `${API_URL}/api/public/c/${clientId}/s/${shipmentId}`,
        { headers: { "X-API-Key": API_KEY } },
      )
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        const err = new Error((body as { error?: string }).error ?? "No encontrado") as Error & { expired?: boolean; notShared?: boolean; status?: number }
        err.expired   = (body as { expired?: boolean }).expired === true
        err.notShared = (body as { notShared?: boolean }).notShared === true
        err.status    = res.status
        throw err
      }
      return res.json() as Promise<PublicShipment>
    },
    enabled: !!clientId && !!shipmentId,
    retry: false,
  })

  const apiError = error as (Error & { expired?: boolean; notShared?: boolean }) | null

  // ── Loading ────────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-muted-foreground">
          <div className="size-8 border-2 border-gray-300 border-t-gray-700 rounded-full animate-spin" />
          <p className="text-sm">Cargando información del viaje…</p>
        </div>
      </div>
    )
  }

  // ── Error ──────────────────────────────────────────────────────────────────
  if (!isLoading && (error || !shipment)) {
    const isExpired   = apiError?.expired   === true
    const isNotShared = apiError?.notShared === true
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <div className={cn(
            "size-14 rounded-full flex items-center justify-center mx-auto mb-4",
            isExpired ? "bg-amber-100" : "bg-red-100",
          )}>
            <AlertTriangleIcon className={cn("size-7", isExpired ? "text-amber-500" : "text-red-500")} />
          </div>
          <h2 className="text-lg font-bold text-gray-900 mb-2">
            {isExpired   ? "Enlace expirado" :
             isNotShared ? "Plan no disponible" :
                           "Viaje no encontrado"}
          </h2>
          <p className="text-sm text-muted-foreground">
            {isExpired
              ? "Este enlace estuvo disponible por 24 horas desde que se guardó el plan. El conductor debe compartirlo nuevamente."
              : isNotShared
              ? "El conductor todavía no ha cargado el plan de viaje."
              : "El enlace no es válido o el viaje no existe."}
          </p>
        </div>
      </div>
    )
  }

  if (!shipment) return null

  const plan = shipment.tripPlan as {
    restHours?: number
    savedAt?: string
    weatherConditions?: Array<{
      day: number
      date: string
      ratings: Record<string, string>
    }>
    route?: {
      origen?: string
      destino?: string
      tipo_movil?: string
      paradas?: Record<string, string[]>
      descansos?: Record<string, boolean>
    } | null
  } | null

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="bg-white border-b">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center gap-3">
          <div className="size-9 rounded-lg bg-indigo-50 flex items-center justify-center shrink-0">
            <MapPinIcon className="size-5 text-indigo-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-muted-foreground">Plan de viaje</p>
            <p className="text-base font-bold truncate">
              {shipment.senderCity ?? "—"} → {shipment.recipientCity ?? "—"}
            </p>
          </div>
          <span className={cn(
            "text-xs font-semibold px-2.5 py-1 rounded-full shrink-0",
            STATUS_COLORS[shipment.status] ?? "bg-gray-100 text-gray-600",
          )}>
            {STATUS_LABELS[shipment.status] ?? shipment.status}
          </span>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-5 space-y-4">

        {/* ── Carga #N ────────────────────────────────────────────────── */}
        <div className="bg-white rounded-2xl border p-4 space-y-1">
          <p className="text-xs font-semibold text-muted-foreground tracking-wide uppercase">
            Carga #{shipment.internalId}
          </p>
          <InfoRow label="Cliente"   value={shipment.clientName} />
          <InfoRow label="Producto"  value={shipment.cargoTypeName} />
          <InfoRow label="Remitente" value={shipment.senderName} />
          <InfoRow label="Destinatario" value={shipment.recipientName} />
          {shipment.deliveryNoteNumber && (
            <InfoRow label="N° Remito" value={shipment.deliveryNoteNumber} />
          )}
          {shipment.loadingWeightKg && (
            <InfoRow label="Peso de carga" value={`${shipment.loadingWeightKg} kg`} />
          )}
          {shipment.unloadingWeightKg && (
            <InfoRow label="Peso de descarga" value={`${shipment.unloadingWeightKg} kg`} />
          )}
        </div>

        {/* ── Móvil ───────────────────────────────────────────────────── */}
        {(shipment.driverName || shipment.truckLicensePlate) && (
          <div className="bg-white rounded-2xl border p-4 space-y-1">
            <p className="text-xs font-semibold text-muted-foreground tracking-wide uppercase flex items-center gap-1.5 mb-2">
              <TruckIcon className="size-3.5" /> Conductor y vehículo
            </p>
            {shipment.driverName && (
              <div className="flex items-center gap-2">
                <div className="size-7 rounded-full bg-gray-100 flex items-center justify-center">
                  <UserIcon className="size-4 text-gray-500" />
                </div>
                <span className="text-sm font-medium">{shipment.driverName}</span>
              </div>
            )}
            {shipment.truckLicensePlate && (
              <p className="text-sm text-muted-foreground font-mono pl-9">
                {shipment.truckLicensePlate}
                {shipment.truckUnitNumber != null && ` · #${shipment.truckUnitNumber}`}
              </p>
            )}
          </div>
        )}

        {/* ── Hitos ───────────────────────────────────────────────────── */}
        <div className="bg-white rounded-2xl border p-4 space-y-3">
          <p className="text-xs font-semibold text-muted-foreground tracking-wide uppercase flex items-center gap-1.5">
            <ClockIcon className="size-3.5" /> Hitos del viaje
          </p>
          <MilestoneRow label="Llegada al origen"  value={shipment.originArrivalAt} />
          <MilestoneRow label="Carga en origen"    value={shipment.originLoadingAt} />
          <MilestoneRow label="Salida de origen"   value={shipment.originDepartureAt} />
          <MilestoneRow label="Llegada al destino" value={shipment.destinationArrivalAt} />
          <MilestoneRow label="Descarga en destino" value={shipment.destinationUnloadingAt} />
        </div>

        {/* ── Plan de viaje ────────────────────────────────────────────── */}
        {plan && (
          <div className="bg-white rounded-2xl border p-4 space-y-4">
            <p className="text-xs font-semibold text-muted-foreground tracking-wide uppercase flex items-center gap-1.5">
              <NavigationIcon className="size-3.5" /> Plan de viaje
            </p>

            {plan.restHours !== undefined && (
              <div className="flex items-center gap-2 rounded-lg bg-muted/50 px-3 py-2 w-fit">
                <MoonIcon className="size-3.5 text-muted-foreground" />
                <span className="text-sm">
                  <span className="font-semibold">{plan.restHours}</span>
                  {" "}h de descanso previo
                </span>
              </div>
            )}

            {plan.route && (
              <div className="space-y-3">
                {/* Route header */}
                <div className="rounded-xl bg-gray-900 px-4 py-3">
                  <p className="text-sm font-bold text-white">
                    {plan.route.origen} → {plan.route.destino}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {plan.route.tipo_movil === "gnc" ? "GNC" : "Diesel"}
                    {plan.route.paradas && ` · ${Object.keys(plan.route.paradas).length} paradas`}
                  </p>
                </div>

                {/* Stops */}
                {plan.route.paradas && Object.entries(plan.route.paradas)
                  .sort(([a], [b]) => Number(a) - Number(b))
                  .map(([num, options]) => (
                    <div key={num}>
                      <div className="flex items-center gap-2 mb-1.5">
                        <div className="size-5 rounded-full bg-gray-900 flex items-center justify-center shrink-0">
                          <span className="text-[10px] font-bold text-white">{num}</span>
                        </div>
                        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                          Parada {num}
                        </span>
                      </div>
                      <div className="space-y-1.5 pl-1">
                        {(options as string[]).map((opt, i) => {
                          const isRest = plan.route?.descansos?.[`${num}:${opt}`] === true
                          return (
                            <div
                              key={i}
                              className={cn(
                                "flex items-center gap-2 rounded-lg px-3 py-2 border text-sm",
                                isRest
                                  ? "bg-indigo-50 border-indigo-200 text-indigo-800 font-medium"
                                  : "bg-green-50 border-green-200 text-emerald-800 font-medium",
                              )}
                            >
                              <MapPinIcon className={cn("size-3.5 shrink-0", isRest ? "text-indigo-500" : "text-emerald-600")} />
                              <span className="flex-1">{opt}</span>
                              {isRest && (
                                <span className="text-[10px] font-bold bg-indigo-100 text-indigo-700 rounded-full px-2 py-0.5">
                                  🌙 Descanso
                                </span>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  ))}
              </div>
            )}

            {plan.weatherConditions && plan.weatherConditions.length > 0 && (
              <div className="space-y-4 pt-2 border-t">
                <p className="text-xs font-semibold text-muted-foreground tracking-wide uppercase">
                  ⛅ Condiciones del viaje
                </p>
                {plan.weatherConditions.map((wday) => (
                  <WeatherDayCard key={wday.day} wday={wday} />
                ))}
              </div>
            )}

            {plan.savedAt && (
              <p className="text-xs text-muted-foreground">
                Guardado el {fmtShort(plan.savedAt)}
              </p>
            )}
          </div>
        )}

        {/* ── Footer ──────────────────────────────────────────────────── */}
        <div className="text-center pb-6">
          <p className="text-xs text-muted-foreground/60">CDC · Sistema de Control de Cargas</p>
        </div>
      </div>
    </div>
  )
}
